import * as React from 'react'
import { ScrollView, View, RefreshControl } from 'react-native'
import styled from '@theme/styled-components'

import Root from '@components/root/Root'
import Header from '@components/header/Header'
import Tabs from '@components/tabs/Tabs'
import Button from '@components/button/Button'
import AlertButton from '@components/button/AlertButton'
import SiteHeader from './SiteHeader'
import DraftsButton from '@components/button/DraftsButton'
import IncidentsHomeSection from './IncidentsHomeSection'
import ManifestationsHomeSection from './ManifestationsHomeSection'
import TenantsHomeSection from './TenantsHomeSection'

import * as UserStore from '@store/user/user'
import * as SiteStore from '@store/site/site'
import * as AuthStore from '@store/auth/auth'

import useI18n from '@i18n/useI18n'
import useReducer from '@store/useReducer'
import useBreakpoint from '@utils/useBreakpoints'
import useNavigation from '@layout/useNavigation'
import useConnectivity from '@utils/useConnectivity'
import useDrafts from '@utils/useDrafts'

import { store, refresh } from '@utils/useSiteChangeHandler'

import { isTomorrow } from 'date-fns'

import { EtatWorkflowIncidentEnum, AuthScope } from '@api/enum'
import { paramsToSearch } from '@api/utils'

const MAIN_VERTICAL_PADDING = 24

interface Props {
  onBack?: () => void
}

const SiteScreen = (props: Props) => {
  const user = useReducer(UserStore.store, (s) => s.user)

  // screen available only for connected user
  if (!user) {
    return null
  }

  return <Screen {...props} user={user} />
}

interface ScreenProps extends Props {
  user: UserResponse
}

const Screen = ({ onBack, user }: ScreenProps) => {
  const i18n = useI18n()
  const navigation = useNavigation()
  const [online, loading] = useConnectivity()

  const siteInfo = useReducer(SiteStore.store, (s) => s.site!)
  const siteStats = useReducer(SiteStore.store, (s) => s.siteStats!)
  const scopes = useReducer(AuthStore.store, (s) => s.scopes)

  const status = useReducer(store, (s) => s.status)
  const draftsCount = useDrafts()[0]

  const [incidentsOffset, setIncidentsOffset] = React.useState(0)
  const [manifestationsOffset, setManifestationsOffset] = React.useState(0)
  const [tenantsOffset, setTenantsOffset] = React.useState(0)
  const [hasScrolled, setHasScrolled] = React.useState(false)

  const scroll = React.useRef<ScrollView>(null)

  const [{ width }, bp] = useBreakpoint()
  const isPhone = bp === 'phone'

  // TODO : not implemented
  const nextActionOnSiteDate = false //getNextActionOnSiteDate(user, siteInfo)

  const onMapPressed = () => navigation.push('/plan')

  const startButton = {
    label: i18n.t('screens.site.startIncident'),
    onClick: () => navigation.push('/incident/start'),
  }

  const actionButton = !!nextActionOnSiteDate
    ? {
        label: isTomorrow(nextActionOnSiteDate)
          ? i18n.t('screens.site.actionsTomorrow')
          : i18n.t('screens.site.actionsDate', { date: nextActionOnSiteDate }),
        onClick: () => undefined,
      }
    : undefined

  const validationButton =
    siteStats &&
    siteStats.enAttenteDeValidation &&
    siteStats.enAttenteDeValidation > 0 &&
    scopes.includes(AuthScope.BandeauPenseBeteValidationLecture)
      ? {
          label: i18n.t('screens.site.validationWanted', { count: siteStats.enAttenteDeValidation }),
          onClick: () =>
            navigation.push(
              `/incidents/list${paramsToSearch({
                filter: EtatWorkflowIncidentEnum.EN_ATTENTE_DE_VALIDATION,
                title: i18n.t('screens.site.validationWanted', { count: siteStats.enAttenteDeValidation }),
              })}`
            ),
        }
      : undefined

  const headerButtons: HeaderButton[] = React.useMemo(() => {
    if (!siteInfo) {
      return []
    }

    /* TODO update missing */
    const buttons: HeaderButton[] = /*siteInfo.mapId*/ true
      ? [
          {
            label: i18n.t('screens.site.plan'),
            onClick: onMapPressed,
            color: 'light',
          },
        ]
      : []

    return [...buttons, startButton]
  }, [siteInfo, i18n])

  const tabsData: TabOption[] = [
    { label: i18n.t('screens.site.incidents'), value: 'incident' },
    { label: i18n.t('screens.site.manifestations'), value: 'manifestations' },
    { label: i18n.t('screens.site.tenants'), value: 'tenants' },
  ]

  const onTabItemSelected = (opt: TabOption) => {
    if (scroll.current) {
      const offset =
        opt.value === 'incident'
          ? incidentsOffset
          : opt.value === 'manifestations'
          ? manifestationsOffset
          : tenantsOffset

      scroll.current.scrollTo({
        y: offset - MAIN_VERTICAL_PADDING,
        animated: true,
      })
    }
  }

  const renderBottomButton = isPhone
    ? () => (
        <BottomButton width={width}>
          <Button status="active" label={startButton.label} onClick={startButton.onClick} />
        </BottomButton>
      )
    : undefined

  const refreshOnPull =
    online && !loading ? (
      <RefreshControl refreshing={status === 'loading'} onRefresh={() => refresh(siteInfo.id, scopes)} />
    ) : undefined

  return (
    <Root
      loading={status === 'loading'}
      renderFixedBottom={renderBottomButton}
      header={siteInfo ? i18n.t(`screens.site.sites.${siteInfo.codeLieu}`) : i18n.t('screens.site.title')}
      renderHeader={
        isPhone
          ? () => (
              <SiteHeader
                hasScrolled={hasScrolled}
                onBack={onBack}
                onMapPressed={onMapPressed}
                actionButton={actionButton}
                validationButton={validationButton}
              />
            )
          : undefined
      }
      onLeft={onBack}
      fixed>
      <ScrollView
        ref={scroll}
        scrollIndicatorInsets={{ right: 1 }}
        keyboardShouldPersistTaps="handled"
        onScroll={(event) => setHasScrolled(event.nativeEvent.contentOffset.y > 0)}
        scrollEventThrottle={16}
        refreshControl={refreshOnPull}>
        {!isPhone && (
          <Header
            siteIcon={siteInfo.codeLieu as SiteIconName}
            title={siteInfo ? i18n.t(`screens.site.sites.${siteInfo.codeLieu}`) : i18n.t('screens.site.title')}
            buttons={headerButtons}
          />
        )}

        <MainContainer topPadding={isPhone && !!actionButton}>
          {!isPhone && !!actionButton && (
            <AlertButtonContainer>
              <AlertButton label={actionButton.label} onClick={actionButton.onClick} />
            </AlertButtonContainer>
          )}
          {!isPhone && !!validationButton && (
            <AlertButtonContainer>
              <AlertButton label={validationButton.label} onClick={validationButton.onClick} style="urgent" />
            </AlertButtonContainer>
          )}

          {draftsCount > 0 && (
            <AlertButtonContainer>
              <DraftsButton count={draftsCount} onClick={() => navigation.push('/drafts')} />
            </AlertButtonContainer>
          )}

          {isPhone && (
            <TabsContainer>
              <Tabs equalTabs={false} data={tabsData} onItemSelected={onTabItemSelected} value={tabsData[0]} />
            </TabsContainer>
          )}

          <View onLayout={(e) => setIncidentsOffset(e.nativeEvent.layout.y)}>
            <IncidentsHomeSection />
          </View>

          <View onLayout={(e) => setManifestationsOffset(e.nativeEvent.layout.y)}>
            <ManifestationsHomeSection showNotOpened={scopes.includes(AuthScope.HomePersonaCoordinateur)} />
          </View>

          <View onLayout={(e) => setTenantsOffset(e.nativeEvent.layout.y)}>
            <TenantsHomeSection />
          </View>
        </MainContainer>
      </ScrollView>
    </Root>
  )
}

export default SiteScreen

const MainContainer = styled(View)<{ topPadding: boolean }>`
  display: flex;
  padding: ${(props) => (props.theme.breakpoint === 'phone' ? `${MAIN_VERTICAL_PADDING}px 16px` : '24px')};
`
const AlertButtonContainer = styled(View)`
  margin-bottom: 32px;
`
const TabsContainer = styled(View)`
  margin-bottom: 16px;
`
const BottomButton = styled(View)<{ width: number }>`
  width: ${(props) => props.width}px;
  background-color: ${(props) => props.theme.colors.background};
  padding: 16px;
`
